import type {GraphQLResponse} from '@github-ui/item-picker-base/api/graphql'
import {DOCUMENT_FIELDS_PATH} from '@github-ui/optimistic-connection'
import {
  createConnectionMutationId,
  dispatchConnectionMutation,
} from '@github-ui/optimistic-connection/connectionMutationBridge'
import {useMutation} from '@github-ui/react-query'
import type {
  MilestoneState,
  UpdateMilestoneMutationMutation,
} from '@github-ui/server-persisted-queries/types/updateMilestoneMutation'
import {reactFetchJSON} from '@github-ui/verified-fetch'

import {LABELS} from './constants/labels'

export type UpdateMilestoneStateVariables = {
  id: string
  number: number
  nameWithOwner: string
  state: MilestoneState
}

async function updateMilestoneState({
  number,
  nameWithOwner,
  state,
}: UpdateMilestoneStateVariables): Promise<UpdateMilestoneMutationMutation> {
  let response: Response
  try {
    // The milestone is identified by `number` in the path; the companion github/github
    // MilestonesController action derives the node id server-side, so it is not sent in the body.
    response = await reactFetchJSON(`/${nameWithOwner}/milestones/${number}/state`, {
      method: 'PUT',
      body: {state},
    })
  } catch {
    throw new Error(LABELS.milestoneEditError)
  }

  // Parse the body once (best-effort). A non-2xx status or a GraphQL `errors` payload both
  // surface the generic edit error, but we thread any server-provided error messages through
  // `cause` so MilestoneEdit's `error.cause` branch can render them via formatFormErrorMessage.
  let result: GraphQLResponse<UpdateMilestoneMutationMutation> | undefined
  try {
    result = (await response.json()) as GraphQLResponse<UpdateMilestoneMutationMutation>
  } catch {
    result = undefined
  }

  const errors = result && 'errors' in result ? result.errors : undefined

  if (!response.ok || (errors && errors.length > 0)) {
    throw errors && errors.length > 0
      ? new Error(LABELS.milestoneEditError, {cause: errors})
      : new Error(LABELS.milestoneEditError)
  }

  if (!result || !('data' in result) || !result.data || !result.data.updateMilestone?.milestone) {
    throw new Error(LABELS.milestoneEditError)
  }

  return result.data
}

/**
 * TanStack Query mutation that toggles a milestone's open/closed state.
 *
 * Publishes a `setFields` operation so overlaying documents get the optimistic `closed` flip.
 */
export function useUpdateMilestoneState() {
  return useMutation<UpdateMilestoneMutationMutation, Error, UpdateMilestoneStateVariables, {mutationId: string}>({
    mutationFn: updateMilestoneState,
    onMutate: ({id, state}) => {
      // One id per invocation: `onError`/`onSuccess` settle by it.
      const mutationId = createConnectionMutationId()
      dispatchConnectionMutation({
        phase: 'optimistic',
        mutationId,
        ownerId: id,
        connectionPath: DOCUMENT_FIELDS_PATH,
        operation: {type: 'setFields', id, fields: {closed: state === 'CLOSED'}},
      })
      return {mutationId}
    },
    onError: (error, _variables, context) => {
      const mutationId = context?.mutationId ?? createConnectionMutationId()
      dispatchConnectionMutation({phase: 'error', mutationId, error})
    },
    onSuccess: (data, _variables, context) => {
      const mutationId = context?.mutationId ?? createConnectionMutationId()
      const milestone = data.updateMilestone?.milestone
      if (!milestone) {
        // Roll the overlay back rather than leaving it pending.
        dispatchConnectionMutation({phase: 'error', mutationId, error: new Error(LABELS.milestoneEditError)})
        return
      }
      dispatchConnectionMutation({
        phase: 'completed',
        mutationId,
        ownerId: milestone.id,
        connectionPath: DOCUMENT_FIELDS_PATH,
        operation: {type: 'setFields', id: milestone.id, fields: {closed: milestone.closed}},
      })
    },
  })
}
